import React from "react";
import * as Sentry from "@sentry/react";
import {createRoutesFromChildren, matchRoutes, Routes, useLocation, useNavigationType} from "react-router-dom";
import {onAuthStateChanged} from "firebase/auth";
import {auth} from "./firebase_services";

// Error tracking with Sentry. Off unless REACT_APP_SENTRY_DSN is set (production builds), so local runs don't report.
// The DSN is public by design: it only lets the app send errors, not read them.
// Imported first in index.tsx, so it starts before the rest of the app loads.
const DSN = process.env.REACT_APP_SENTRY_DSN;

const stripQuery = (url: string) => url.split(/[?#]/)[0];

// Sentry's page speed measurements (web-vitals) use Array.prototype.at and crash on browsers without it
// (Safari before 15.4, old link scanners). Those browsers still report errors, just no performance traces.
const SUPPORTS_TRACING = typeof (Array.prototype as any).at === 'function';

if (DSN) {
    Sentry.init({
        dsn: DSN,
        environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
        integrations: SUPPORTS_TRACING ? [
            // Page loads and navigations, named by route (e.g. /settings)
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ] : [],
        // Performance traces for 10% of page loads, enough to spot slow screens
        tracesSampleRate: 0.1,
        // No trace headers on API calls: the API's CORS settings only allow Content-Type and Authorization
        tracePropagationTargets: [],
        // No IP addresses, emails, cookies, headers, request bodies, query strings or local variables:
        // the user is identified by Firebase uid only. No session replay.
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: false,
            httpBodies: [],
            urlQueryParams: false,
            genAI: {inputs: false, outputs: false},
            stackFrameVariables: false,
        },
        // Noise from browser extensions and dropped connections, not from the app
        // ".at is not a function": web-vitals on a browser without Array.prototype.at (see SUPPORTS_TRACING)
        ignoreErrors: ['ResizeObserver loop', 'Network Error', 'Load failed', 'Failed to fetch', /\.at is not a function/],
        // Query strings can hold one-time codes (e.g. ?oobCode= in /auth/action password reset links)
        beforeSend(event) {
            if (event.request?.url) event.request.url = stripQuery(event.request.url);
            return event;
        },
        beforeSendTransaction(event) {
            if (event.request?.url) event.request.url = stripQuery(event.request.url);
            return event;
        },
        beforeBreadcrumb(breadcrumb) {
            const data = breadcrumb.data;
            if (data) {
                for (const key of ['from', 'to', 'url']) {
                    if (typeof data[key] === 'string') data[key] = stripQuery(data[key]);
                }
            }
            return breadcrumb;
        },
    });
    // Errors show which user hit them (uid only)
    onAuthStateChanged(auth, (user) => Sentry.setUser(user ? {id: user.uid} : null));
}

// Routes wrapped so navigations are named by route; works as plain <Routes> when Sentry is off
export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// API failures on the server side (5xx) are worth seeing even when the page handles them with a toast
export const reportApiError = (error: any) => {
    if (!DSN) return;
    const status = error?.response?.status;
    if (!status || status < 500) return;
    Sentry.captureException(error, {
        tags: {api_status: String(status)},
        extra: {method: error.config?.method, url: error.config?.url && stripQuery(error.config.url), code: error.response?.data?.code},
    });
};

export {Sentry};
